// Phase 2 P2-CG (Cost Governance Framework v1.0) — useCostDashboard hook.
//
// Pulls platform-wide cost metrics for the admin dashboard:
//   - daily_spend                — last 14 days totals + counts
//   - agent_spend_by_function    — per-function-per-model rollup, last 30d
//   - agent_governance_config    — daily ceiling + warning threshold
//   - check_circuit_breaker RPC  — current breaker state
//
// All sources are platform-level aggregates (no user PII), exposed to
// authenticated users via the cost_dashboard_authenticated_read migration.

import { useCallback, useMemo } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { useComposedQuery } from "../data/useTableQuery.ts";

export interface DailySpendRow {
  date: string;
  total_usd: number | null;
  total_calls: number | null;
  fallback_calls: number | null;
  throttled_calls: number | null;
  blocked_calls: number | null;
}

export interface SpendByFunctionRow {
  date: string;
  function_name: string;
  model_used: string;
  calls: number;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
}

export interface GovernanceConfig {
  daily_ceiling_usd: number;
  warning_threshold_pct: number;
  monthly_cap_anthropic: number;
  monthly_cap_openai: number;
  pilot_phase: boolean;
  fallback_alert_pct: number;
}

export interface BreakerStatus {
  allowed: boolean;
  reason?: string;
  spent: number;
  ceiling: number;
  pct: number;
  remaining?: number;
}

export interface CostDashboardState {
  dailyHistory: DailySpendRow[];
  byFunction: SpendByFunctionRow[];
  config: GovernanceConfig | null;
  breaker: BreakerStatus | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface CostDashboardComposite {
  dailyHistory: DailySpendRow[];
  byFunction: SpendByFunctionRow[];
  config: GovernanceConfig | null;
  breaker: BreakerStatus | null;
  error: string | null;
}

const EMPTY_COMPOSITE: CostDashboardComposite = {
  dailyHistory: [],
  byFunction: [],
  config: null,
  breaker: null,
  error: null,
};

export function useCostDashboard(): CostDashboardState {
  const { user } = useAuthSession();

  const q = useComposedQuery<CostDashboardComposite>(
    ["cost-dashboard", user?.id ?? null],
    async (client) => {
      // Generated Database types don't include the cost-governance views; cast to any.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = client as any;

      const cutoff = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);
      const cutoff30 = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);

      const [dailyRes, fnRes, configRes, breakerRes] = await Promise.all([
        sb
          .from("daily_spend")
          .select("*")
          .gte("date", cutoff)
          .order("date", { ascending: false }),
        sb
          .from("agent_spend_by_function")
          .select("*")
          .gte("date", cutoff30)
          .order("cost_usd", { ascending: false }),
        sb.from("agent_governance_config").select("*").eq("id", 1).maybeSingle(),
        sb.rpc("check_circuit_breaker", { p_priority: "MEDIUM" }),
      ]);

      const errs = [dailyRes.error, fnRes.error, configRes.error, breakerRes.error]
        .filter((e: { message?: string } | null): e is { message: string } => Boolean(e && e.message))
        .map((e) => e.message);

      return {
        dailyHistory: (dailyRes.data ?? []) as DailySpendRow[],
        byFunction: (fnRes.data ?? []) as SpendByFunctionRow[],
        config: (configRes.data as GovernanceConfig | null) ?? null,
        breaker: (breakerRes.data as BreakerStatus | null) ?? null,
        error: errs.length > 0 ? errs.join(" · ") : null,
      };
    },
    { enabled: Boolean(user) },
  );

  const refetch = useCallback(async (): Promise<void> => {
    await q.refetch();
  }, [q]);

  return useMemo<CostDashboardState>(() => {
    const c = q.data ?? EMPTY_COMPOSITE;
    return {
      dailyHistory: c.dailyHistory,
      byFunction: c.byFunction,
      config: c.config,
      breaker: c.breaker,
      loading: q.isLoading,
      error: c.error,
      refetch,
    };
  }, [q.data, q.isLoading, refetch]);
}
